import { LuMail, LuLock } from 'react-icons/lu';
import GithubIcon from 'assets/Github.svg';
import { GIT_PROVIDERS } from '../../consts/git';
import { useNavigateWithQueryParams } from '../../hooks/useNavigateWithQueryParams';
import { Google } from '../../logos';
import { OIDCProvider } from '../../network/httpClients/OIDCAuthenticationClient/api.schema';
import { OIDC_PROVIDER } from '../../network/httpClients/OIDCAuthenticationClient/consts';
import { ROUTES } from '../../routes/consts';
import { mixpanelService } from '../../services/mixpanel';
import {
  MIXPANEL_EVENT_NAMES,
  MIXPANEL_OPERATIONS,
} from '../../services/mixpanel/consts';
import { buildMixpanelErrorData } from '../../services/mixpanel/utils';
import { handleOIDCError } from '../../utils/authentication';
import { ProviderConfig } from './types';
import { getGithubLoginUrl, getGoogleLoginUrl } from './utils';

const ALL_PROVIDER_CONFIGS = (
  onLoginMethodClick: (url: string, provider: OIDCProvider) => Promise<void>,
  onNavigate: (route: string) => void,
): ProviderConfig[] => [
  {
    provider: GIT_PROVIDERS.GITHUB,
    icon: <img src={GithubIcon} width={20} alt={GIT_PROVIDERS.GITHUB} />,
    className: 'border-purple-muted-500',
    onClick: async () => {
      const url = getGithubLoginUrl();

      await onLoginMethodClick(url, OIDC_PROVIDER.GITHUB);
    },
  },
  {
    provider: OIDC_PROVIDER.GOOGLE,
    icon: <Google size={18} />,
    className: 'border-purple-muted-500',
    onClick: async () => {
      const url = getGoogleLoginUrl();

      await onLoginMethodClick(url, OIDC_PROVIDER.GOOGLE);
    },
  },
  {
    provider: OIDC_PROVIDER.EMAIL,
    icon: <LuMail size={18} />,
    className: 'border-purple-muted-500',
    onClick: () => {
      onNavigate(ROUTES.EMAIL);
    },
  },
  {
    provider: 'sso' as const,
    icon: <LuLock size={18} />,
    className: 'border-purple-muted-500',
    onClick: () => {
      onNavigate(ROUTES.SSO);
    },
  },
];

export const useProvidersConfiguration = (): {
  providers: ProviderConfig[];
} => {
  const { navigateWithQueryParams } = useNavigateWithQueryParams();

  const onLoginMethodClick = async (url: string, provider: OIDCProvider) => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        redirect: 'manual',
        headers: {
          Accept: 'application/json',
        },
      });

      if (response.type === 'opaqueredirect') {
        window.location.href = url;

        return;
      }

      const errorData = await response.json();

      throw errorData;
    } catch (err: unknown) {
      mixpanelService.track(MIXPANEL_EVENT_NAMES.ERROR, {
        operation: MIXPANEL_OPERATIONS.LOGIN_INTERRUPTED,
        error_data: buildMixpanelErrorData(err, provider),
      });
      handleOIDCError(err);
    }
  };

  const providers = ALL_PROVIDER_CONFIGS(
    onLoginMethodClick,
    navigateWithQueryParams,
  );

  return { providers };
};
